import Box from '@mui/material/Box';
import { styled, css, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';
import { Outlet, useParams } from 'react-router-dom';

import LandingContentSkeleton, {
  LandingBreadcrumbSkeleton,
} from './landing-content-skeleton';
import LeftNav from './left-nav';
import RightPanel from './right-panel';
import { AssessmentProgressProvider } from '../assessment/assessment-progress-context';
import BottomNav, { BOTTOM_NAV_HEIGHT } from '../base/bottom-nav';
import TopBar, { TOP_BAR_HEIGHT } from '../base/top-bar';
import { AIResourceProvider } from '../project/dashboard/ai-resources-context';
import {
  DashboardDataProvider,
  useDashboardDataContext,
} from '../project/dashboard/dashboard-context';
import DynamicBreadcrumb from '../project/dynamic-breadcrumb';

import { ReportJobsProviderWrapper } from '~/client/contexts/report-jobs-context';
import { ProjectPolicyLeftNavContextProvider } from '~c/components/add-project-policy/left-nav/left-nav-context';
import { LeftNavContextProvider } from '~c/components/base/left-nav';

interface Props {
  className?: string;
}

interface LandingContentAreaProps {
  showTopBar: boolean;
  contentPaddingTop: string | { xs: string; sm: string; md: string };
  isMobile: boolean;
}

const LandingContentArea: React.FC<LandingContentAreaProps> = ({
  showTopBar,
  contentPaddingTop,
  isMobile,
}) => {
  const { loading } = useDashboardDataContext();
  const {
    projectId,
    functionalModelId,
    candidateId,
    vendorModelId,
    vendorAgentId,
    agentId,
  } = useParams();

  const effectiveId =
    candidateId ??
    agentId ??
    functionalModelId ??
    vendorModelId ??
    vendorAgentId ??
    projectId;
  const showContentSkeleton = loading && Boolean(effectiveId);

  const mobileBreadcrumb = showContentSkeleton ? (
    <LandingBreadcrumbSkeleton compact />
  ) : (
    <DynamicBreadcrumb compact inTopBar />
  );

  const desktopBreadcrumb = showContentSkeleton ? (
    <LandingBreadcrumbSkeleton />
  ) : (
    <DynamicBreadcrumb />
  );

  return (
    <Box className="content-wrapper">
      <TopBar>{mobileBreadcrumb}</TopBar>
      {showTopBar && (
        <Box
          component="span"
          sx={{
            display: 'block',
            height: `${TOP_BAR_HEIGHT}px`,
            flexShrink: 0,
          }}
          aria-hidden
        />
      )}
      <Box
        className="content"
        sx={{
          paddingTop: contentPaddingTop,
          paddingBottom: isMobile ? `${BOTTOM_NAV_HEIGHT + 16}px` : undefined,
        }}
      >
        {showTopBar ? null : desktopBreadcrumb}
        {showContentSkeleton ? <LandingContentSkeleton /> : <Outlet />}
      </Box>
    </Box>
  );
};

const Landing: React.FC<Props> = ({ className }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const showTopBar = isMobile;

  const contentPaddingTop = showTopBar
    ? '16px'
    : { xs: '16px', sm: '24px', md: '50px' };

  return (
    <div className={className}>
      <LeftNavContextProvider>
        <ProjectPolicyLeftNavContextProvider>
          <DashboardDataProvider>
            <AIResourceProvider>
              <AssessmentProgressProvider>
                <ReportJobsProviderWrapper>
                  <LeftNav />
                  <LandingContentArea
                    showTopBar={showTopBar}
                    contentPaddingTop={contentPaddingTop}
                    isMobile={isMobile}
                  />
                  <RightPanel />
                  <BottomNav />
                </ReportJobsProviderWrapper>
              </AssessmentProgressProvider>
            </AIResourceProvider>
          </DashboardDataProvider>
        </ProjectPolicyLeftNavContextProvider>
      </LeftNavContextProvider>
    </div>
  );
};

export default styled(Landing)(
  ({ theme }) => css`
    display: flex;
    .content-wrapper {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 0;
      position: relative;
    }
    .content {
      flex: 1;
      min-width: 0;
      overflow-x: hidden;
      overflow-y: auto;
      border-radius: 0;
      background-color: ${theme.palette.background.default};
      pointer-events: auto;

      ${theme.breakpoints.up('md')} {
        padding-left: 50px;
        padding-right: 50px;
        padding-bottom: 50px;
      }
      ${theme.breakpoints.between('sm', 'md')} {
        padding-left: 24px;
        padding-right: 24px;
        padding-bottom: 24px;
      }
      ${theme.breakpoints.down('sm')} {
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 16px;
      }
    }
  `
);
